import { Link, useLocation } from 'react-router-dom';

import { ROLE_LABELS } from '@/lib/format';
import { Button, Icon, PageHeader } from '@/components';
import { landingPathFor, ROUTES } from '@/lib/routes';
import { useSession } from '@/lib/session';

interface LocationState {
  from?: { pathname?: string };
}

/**
 * Where a role guard sends someone.
 *
 * This is reached only when a signed-in person navigates to a screen their
 * role cannot use - a contractor typing `/work`, say. It exists so that case
 * produces a sentence rather than a screen full of failed requests.
 *
 * It says which role you are signed in as, because the commonest cause is
 * being signed in as the wrong person.
 */
export default function Forbidden() {
  const { user } = useSession();
  const location = useLocation();
  const attempted = (location.state as LocationState | null)?.from?.pathname;

  const home = user ? landingPathFor(user.role) : ROUTES.signIn;

  return (
    <>
      <PageHeader
        title="Not your screen"
        description="Your account does not have access to this part of the portal."
      />

      <div className="max-w-xl rise">
        <div className="flex items-start gap-3 rounded-lg border border-warn/25 bg-warn-soft px-4 py-3 text-warn-ink">
          <Icon name="alert" size={16} className="mt-0.5" />
          <div className="text-small leading-relaxed">
            {attempted ? (
              <p>
                <span className="font-mono">{attempted}</span> is restricted.
              </p>
            ) : null}
            {user ? (
              <p className={attempted ? 'mt-1' : undefined}>
                You are signed in as <span className="font-medium">{user.name}</span> (
                {ROLE_LABELS[user.role]}).
              </p>
            ) : null}
          </div>
        </div>

        <p className="mt-4 text-ui text-muted">
          Roles are decided by the API, not by this page - so nothing here would have loaded even if
          the link had worked. If you think your account should have access, Ryan can change it.
        </p>

        <div className="mt-6">
          <Link to={home}>
            <Button variant="primary">Back to my home screen</Button>
          </Link>
        </div>
      </div>
    </>
  );
}
